/**
 * Documento de transferencia de chasis entre sucursales, en hoja carta y con
 * el mismo estilo de casa del conduce (impresionfacturamotor.js): membrete
 * con logo a la izquierda, datos del documento a la derecha, ruta origen →
 * destino, tabla de unidades con cabecera navy y firmas de despacho,
 * transporte y recepción.
 *
 * /api/creartransferencia devuelve {id, fecha, origen, destino, compania};
 * con un backend viejo (respuesta "success") se cae al nombre del negocio
 * guardado en el login y a la fecha del equipo.
 */
import { imprimirHtml } from "lib/impresionhtml";

const NAVY = "#18586b";

const esc = (s) =>
  String(s ?? "").replace(
    /[<>&"]/g,
    (ch) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;" }[ch])
  );

// Logo y pie: solo data URLs de imagen (lo que guarda configuración).
const imagen = (v) => (typeof v === "string" && v.startsWith("data:image/") ? v : "");

// "2-Santiago" → {codigo: "2", nombre: "Santiago"}; sin guion, todo es nombre.
const partirSucursal = (s) => {
  const txt = String(s ?? "").trim();
  const i = txt.indexOf("-");
  return i < 0
    ? { codigo: "", nombre: txt }
    : { codigo: txt.slice(0, i).trim(), nombre: txt.slice(i + 1).trim() };
};

const nombreUtil = (v) => {
  const s = String(v ?? "").trim();
  return s && s !== "false" && s !== "null" && s !== "undefined" ? s : "";
};

const sucursalHtml = (rotulo, s, clase = "") => {
  const { codigo, nombre } = partirSucursal(s);
  return `<div class="punto ${clase}">
      <span>${esc(rotulo)}</span>
      <b>${esc(nombre || "—")}</b>
      ${codigo ? `<small>Sucursal ${esc(codigo)}</small>` : ""}
    </div>`;
};

/**
 * @param {string} sucursal        Sucursal destino ("codigo-nombre").
 * @param {string} nombrecompania  Nombre del negocio (respaldo del membrete).
 * @param {Array}  dataresultados  Chasis transferidos {Chasis, Marca, Modelo, Color, condicion}.
 * @param {string} titulo          Título del documento.
 * @param {Object} [extra]         {origen, transportista, comentario, usuario, logo, respuesta}
 *                                 — `respuesta` es el cuerpo de /api/creartransferencia;
 *                                 `logo` (de /api/empresa_logo) cubre el membrete sin logo.
 */
export const PlantillaTransferencia = (
  sucursal,
  nombrecompania,
  dataresultados,
  titulo,
  extra = {}
) => {
  const resp = extra.respuesta && typeof extra.respuesta === "object" ? extra.respuesta : {};
  const c = resp.compania && typeof resp.compania === "object" ? resp.compania : {};
  const empresa =
    nombreUtil(c.nombre) ||
    nombreUtil(nombrecompania) ||
    nombreUtil(localStorage.getItem("nombrenegocio"));
  const logo = imagen(c.logo) || imagen(extra.logo);
  const pie = imagen(c.footer);

  const origen = resp.origen || extra.origen || "";
  const destino = resp.destino || sucursal || "";
  const fecha =
    resp.fecha || new Date().toLocaleString("es-DO", { dateStyle: "short", timeStyle: "short" });
  const referencia = resp.id ? String(resp.id).slice(0, 8).toUpperCase() : "";
  const unidades = dataresultados || [];

  const filas = unidades
    .map(
      (item, i) => `<tr>
        <td class="n">${i + 1}</td>
        <td class="chasis">${esc(item.Chasis)}</td>
        <td>${esc(item.Marca)}</td>
        <td>${esc(item.Modelo)}</td>
        <td>${esc(item.Color)}</td>
        <td>${esc(item.condicion)}</td>
      </tr>`
    )
    .join("");

  const datos = [
    ["Transportista", extra.transportista],
    ["Despachado por", extra.usuario],
  ]
    .filter(([, v]) => nombreUtil(v))
    .map(([k, v]) => `<div><span>${esc(k)}</span><b>${esc(v)}</b></div>`)
    .join("");

  const html = `
    <html><head><meta charset="utf-8"><meta name="color-scheme" content="light"><title>${esc(
      titulo || "Transferencia"
    )}</title>
    <style>
      @page { size: letter; margin: 14mm; }
      * { box-sizing: border-box; }
      body { background: #fff; color: #1a2430; font-family: Arial, Helvetica, sans-serif; font-size: 12px; margin: 0;
             -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .cab { display: flex; justify-content: space-between; align-items: flex-start; gap: 16px;
             border-bottom: 3px solid ${NAVY}; padding-bottom: 12px; }
      .emp { display: flex; align-items: flex-start; gap: 12px; }
      .emp img.logo { max-height: 80px; max-width: 220px; object-fit: contain; }
      .emp h1 { margin: 0; font-size: 20px; color: ${NAVY}; }
      .emp p { margin: 2px 0 0; color: #4a5866; font-size: 11px; }
      .doc { text-align: right; min-width: 200px; }
      .doc .titulo { font-size: 15px; font-weight: bold; color: ${NAVY}; letter-spacing: 0.5px; text-transform: uppercase; }
      .doc p { margin: 3px 0 0; font-size: 11px; color: #4a5866; }
      .doc b { color: #1a2430; }
      .ruta { display: flex; align-items: stretch; gap: 0; margin: 16px 0 12px; border: 1px solid #d7dee5;
              border-radius: 8px; overflow: hidden; }
      .punto { flex: 1; padding: 10px 16px; }
      .punto span { display: block; font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.06em; }
      .punto b { display: block; font-size: 16px; color: ${NAVY}; margin-top: 2px; }
      .punto small { color: #4a5866; font-size: 10px; }
      .flecha { display: flex; align-items: center; justify-content: center; width: 56px; background: ${NAVY};
                color: #fff; font-size: 22px; font-weight: bold; }
      .punto.destino { background: #f0f5f8; text-align: right; }
      .bloque { margin: 0 0 12px; border: 1px solid #d7dee5; border-radius: 6px; padding: 8px 14px;
                display: flex; gap: 30px; flex-wrap: wrap; }
      .bloque span { font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; display: block; }
      .bloque b { font-size: 12px; }
      .rotulo { display: flex; justify-content: space-between; align-items: baseline; margin: 14px 0 4px;
                font-weight: bold; color: ${NAVY}; font-size: 12px; text-transform: uppercase; }
      .rotulo small { font-weight: normal; color: #4a5866; text-transform: none; font-size: 11px; }
      table.lineas { width: 100%; border-collapse: collapse; }
      table.lineas th { background: ${NAVY}; color: #fff; padding: 7px 8px; font-size: 11px; text-align: left; }
      table.lineas td { padding: 6px 8px; border-bottom: 1px solid #e3e9ee; font-size: 11px; }
      table.lineas tr:nth-child(even) td { background: #f5f8fa; }
      table.lineas tr { break-inside: avoid; page-break-inside: avoid; }
      td.n, th.n { width: 28px; text-align: center; color: #4a5866; }
      td.chasis { font-family: "Courier New", monospace; font-weight: bold; letter-spacing: 0.03em; }
      tr.tot td { font-weight: bold; border-top: 2px solid ${NAVY}; border-bottom: none; background: #f0f5f8 !important; color: ${NAVY}; }
      .nota { margin-top: 12px; border-left: 4px solid ${NAVY}; background: #f5f8fa; padding: 8px 12px; font-size: 11px; }
      .nota span { display: block; font-size: 10px; color: #4a5866; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 2px; }
      .firmas { display: flex; justify-content: space-between; gap: 24px; margin-top: 64px; break-inside: avoid; }
      .firmas div { flex: 1; border-top: 1px solid #1a2430; text-align: center; padding-top: 5px; font-size: 11px; color: #4a5866; }
      .firmas small { display: block; font-size: 10px; margin-top: 2px; }
      .pie { position: fixed; bottom: 0; left: 0; right: 0; }
      .pie img { display: block; width: 100%; }
    </style></head>
    <body>
      <div class="cab">
        <div class="emp">
          ${logo ? `<img class="logo" src="${logo}" alt="" />` : ""}
          <div>
            <h1>${esc(empresa)}</h1>
            ${c.direccion ? `<p>${esc(c.direccion)}</p>` : ""}
            <p>${[c.rnc ? `RNC: ${esc(c.rnc)}` : "", c.telefono ? `Tel: ${esc(c.telefono)}` : ""]
              .filter(Boolean)
              .join(" · ")}</p>
          </div>
        </div>
        <div class="doc">
          <div class="titulo">Transferencia de vehículos</div>
          ${referencia ? `<p>Ref.: <b>${esc(referencia)}</b></p>` : ""}
          <p>Fecha: <b>${esc(fecha)}</b></p>
          <p>Estado: <b>En tránsito</b></p>
        </div>
      </div>

      <div class="ruta">
        ${sucursalHtml("Desde", origen)}
        <div class="flecha">&#8594;</div>
        ${sucursalHtml("Recibir en", destino, "destino")}
      </div>

      ${datos ? `<div class="bloque">${datos}</div>` : ""}

      <div class="rotulo">
        <span>${esc(titulo || "Chasis transferidos")}</span>
        <small>${unidades.length} ${unidades.length === 1 ? "unidad" : "unidades"}</small>
      </div>
      <table class="lineas">
        <tr>
          <th class="n">#</th><th>Chasis</th><th>Marca</th><th>Modelo</th><th>Color</th><th>Condición</th>
        </tr>
        ${filas}
        <tr class="tot"><td></td><td colspan="5">Total de unidades: ${unidades.length}</td></tr>
      </table>

      ${
        nombreUtil(extra.comentario)
          ? `<div class="nota"><span>Comentario</span>${esc(extra.comentario)}</div>`
          : ""
      }

      <div class="firmas">
        <div>Despachado por<small>${esc(partirSucursal(origen).nombre)}</small></div>
        <div>Transportado por<small>${esc(nombreUtil(extra.transportista))}</small></div>
        <div>Recibido por<small>${esc(partirSucursal(destino).nombre)}</small></div>
      </div>
      ${pie ? `<div class="pie"><img src="${pie}" alt="" /></div>` : ""}
      <script>window.onload=function(){window.print();}</script>
    </body></html>`;
  imprimirHtml(html);
};

export default PlantillaTransferencia;
